import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useRaffleDraws } from '../../hooks/useRaffles';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { Card } from '../../components/common/Card';
import { Button } from '../../components/common/Button';
import { Spinner } from '../../components/common/Spinner';
import { Input } from '../../components/common/Input';
import { Select } from '../../components/common/Select';
import { formatCurrency, formatDate, formatPercentage } from '../../utils/formatters';

export const AdminDrawHistory: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [page, setPage] = useState(1);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [sortBy, setSortBy] = useState('drawDate');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  const { data, isLoading } = useRaffleDraws(id!, { page, limit: 10, startDate: startDate || undefined, endDate: endDate || undefined, sortBy, sortOrder });

  if (isLoading) {
    return <AdminLayout><div className="flex justify-center py-12"><Spinner size="lg" /></div></AdminLayout>;
  }

  const draws = data?.data?.draws || [];
  const pagination = data?.data?.pagination;

  return (
    <AdminLayout>
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">Draw History</h1>
        <p className="text-gray-500 dark:text-gray-400 mt-1">Past draw results and distributions</p>
      </div>
      <Card className="mb-6">
        <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-4">Filters</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Input label="Start Date" type="date" value={startDate} onChange={(e) => { setStartDate(e.target.value); setPage(1); }} />
          <Input label="End Date" type="date" value={endDate} onChange={(e) => { setEndDate(e.target.value); setPage(1); }} />
          <Select label="Sort By" value={sortBy} onChange={(e) => { setSortBy(e.target.value); setPage(1); }} options={[{ value: 'drawDate', label: 'Draw Date' }, { value: 'totalSales', label: 'Total Sales' }]} />
          <Select label="Order" value={sortOrder} onChange={(e) => { setSortOrder(e.target.value as 'asc' | 'desc'); setPage(1); }} options={[{ value: 'desc', label: 'Descending' }, { value: 'asc', label: 'Ascending' }]} />
        </div>
      </Card>

      {draws.length === 0 ? (
        <Card><p className="text-center text-gray-400 dark:text-gray-500 py-12">No draws found.</p></Card>
      ) : (
        <>
          <div className="space-y-5">
            {draws.map((draw) => (
              <Card key={draw.id} className="hover:shadow-md transition-shadow">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">Draw #{draw.id.slice(0, 8)}</h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400">{formatDate(draw.drawDate)}</p>
                  </div>
                  <div className="sm:text-right mt-2 sm:mt-0">
                    <p className="text-xs text-gray-500 dark:text-gray-400">Total Sales</p>
                    <p className="text-2xl font-bold text-indigo-600">{formatCurrency(draw.totalSales, draw.currency)}</p>
                  </div>
                </div>
                <div className="border-t border-gray-100 dark:border-gray-800 pt-4 mb-4">
                  <h4 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">Winners</h4>
                  <div className="space-y-2">
                    {draw.winners.map((winner, index) => (
                      <div key={index} className="flex items-center justify-between bg-gray-50 dark:bg-gray-800 p-3 rounded-xl">
                        <div className="flex items-center gap-3">
                          <div className="bg-indigo-100 dark:bg-indigo-900/50 text-indigo-700 dark:text-indigo-400 w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold">{index + 1}</div>
                          <div>
                            <p className="font-medium text-gray-900 dark:text-gray-100">{winner.name}</p>
                            {winner.ticketNumber && <p className="text-xs text-gray-500 dark:text-gray-400 font-mono">Ticket #{winner.ticketNumber}</p>}
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-semibold text-gray-900 dark:text-gray-100">{formatCurrency(winner.prizeAmount, draw.currency)}</p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">{formatPercentage(winner.prizePercentage)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="border-t border-gray-100 dark:border-gray-800 pt-4">
                  <h4 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">Distribution</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-3">
                      <p className="text-xs text-gray-500 dark:text-gray-400">Organizer Share</p>
                      <p className="font-semibold text-gray-900 dark:text-gray-100 mt-0.5">{formatCurrency(draw.organizerShare, draw.currency)} ({formatPercentage(draw.organizerSharePercentage)})</p>
                    </div>
                    <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-3">
                      <p className="text-xs text-gray-500 dark:text-gray-400">Platform Fee</p>
                      <p className="font-semibold text-gray-900 dark:text-gray-100 mt-0.5">{formatCurrency(draw.platformFee, draw.currency)} ({formatPercentage(draw.platformFeePercentage)})</p>
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
          {pagination && pagination.pages > 1 && (
            <div className="flex justify-center items-center gap-4 mt-8">
              <Button variant="outline" disabled={page === 1} onClick={() => { setPage(page - 1); window.scrollTo({ top: 0, behavior: 'smooth' }); }}>Previous</Button>
              <span className="text-sm text-gray-500 dark:text-gray-400">Page {page} of {pagination.pages}</span>
              <Button variant="outline" disabled={page === pagination.pages} onClick={() => { setPage(page + 1); window.scrollTo({ top: 0, behavior: 'smooth' }); }}>Next</Button>
            </div>
          )}
        </>
      )}
    </AdminLayout>
  );
};
